import React, { useEffect, useRef } from 'react';
import * as fabric from 'fabric';

interface ScrollerProps {
  content: string;
  speed: 'slow' | 'medium' | 'fast' | number;
  direction: 'ltr' | 'rtl';
  textColor: string;
  backgroundColor: string;
  fontFamily: string;
  fontSize?: number;
  letterSpacing?: number;
  width: number;
  height: number;
  currentTime: number;
  isPlaying: boolean;
}

const SPEED_VALS = {
  slow: 2,
  medium: 8,
  fast: 24
};

export const Scroller: React.FC<ScrollerProps> = ({
  content,
  speed,
  direction,
  textColor,
  backgroundColor,
  fontFamily,
  fontSize = 24,
  letterSpacing = 0,
  width,
  height,
  currentTime,

}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<any>(null);
  const textGroupRef = useRef<any>(null);

  // Initialize Canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    // Use wildcard import and check for Canvas/StaticCanvas
    const FabricCanvas = (fabric as any).StaticCanvas || (fabric as any).fabric?.StaticCanvas;

    if (!FabricCanvas) {
      console.error("Fabric.js StaticCanvas not found");
      return;
    }

    const canvas = new FabricCanvas(canvasRef.current, {
      width: Math.floor(width),
      height: Math.floor(height),
      backgroundColor: backgroundColor,
      renderOnAddRemove: false
    });

    fabricRef.current = canvas;

    return () => {
      canvas.dispose();
      fabricRef.current = null;
    };
  }, [width, height, backgroundColor]);

  // Handle Text Creation
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    if (textGroupRef.current) {
      canvas.remove(textGroupRef.current);
    }

    const FabricText = (fabric as any).Text || (fabric as any).fabric?.Text;
    const FabricGroup = (fabric as any).Group || (fabric as any).fabric?.Group;

    if (!FabricText || !FabricGroup) return;

    const activeFont = fontFamily || 'Bricolage Grotesque';
    const charSpacing = (letterSpacing / fontSize) * 1000;

    const measureText = new FabricText(content, {
      fontFamily: activeFont,
      fontSize: fontSize,
      fontWeight: '600',
      charSpacing: charSpacing
    });

    const textWidth = measureText.width || 0;
    const padding = fontSize * 3;
    const totalItemWidth = textWidth + padding;

    const copiesNeeded = Math.ceil(width / totalItemWidth) + 2;
    const textObjects: any[] = [];

    for (let i = 0; i < copiesNeeded; i++) {
      const t = new FabricText(content, {
        left: i * totalItemWidth,
        top: 0,
        originX: 'left',
        originY: 'center',
        fontFamily: activeFont,
        fontSize: fontSize,
        fill: textColor,
        fontWeight: '600',
        charSpacing: charSpacing
      });
      textObjects.push(t);
    }

    const group = new FabricGroup(textObjects, {
      selectable: false,
      evented: false,
      originX: 'left',
      originY: 'center',
      top: height / 2,
      left: 0
    });

    canvas.add(group);
    textGroupRef.current = group;
    canvas.renderAll();
  }, [content, fontFamily, fontSize, letterSpacing, textColor, width, height]);

  // Sync with Timeline Playhead (Phase 2)
  useEffect(() => {
    const canvas = fabricRef.current;
    const group = textGroupRef.current;
    if (!canvas || !group) return;

    const numericSpeed = typeof speed === 'number' ? speed : SPEED_VALS[speed as keyof typeof SPEED_VALS] || 8;

    const firstItem = group.item(0);
    if (!firstItem) return;

    const itemWidth = (firstItem.width || 0) + (fontSize * 3);
    const pixelsPerMs = numericSpeed / 20;
    const displacement = (currentTime * pixelsPerMs) % itemWidth;

    const isRTL = direction === 'rtl';

    if (isRTL) {
      group.set({ left: -displacement });
    } else {
      group.set({ left: -itemWidth + displacement });
    }

    canvas.renderAll();
  }, [currentTime, speed, direction, fontSize]);

  return (
    <div className="w-full h-full overflow-hidden" style={{ width, height }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

